/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Combobox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Divider,
    Dropdown,
    Field,
    makeStyles,
    Option,
    shorthands,
    Tab,
    TabList,
    Toaster,
    tokens,
    type SelectTabData,
    type SelectTabEvent,
} from '@fluentui/react-components';
import { DeleteRegular, PersonAddRegular } from '@fluentui/react-icons';
import React, { useEffect, useState } from 'react';
import type { IDataItem } from '../../models/IDataItem';
import type { IUserPermissionItem } from '../../models/IUserPermissionItem';
import { DataService } from '../../services/dataService';

const useStyles = makeStyles({
    container: {
        padding: `0 ${tokens.spacingHorizontalL}`,
        height: '100%',
        overflow: 'auto',
    },
    overviewSection: {
        marginTop: tokens.spacingVerticalL,
        marginBottom: tokens.spacingVerticalXXL,
    },
    tabContent: {
        marginTop: tokens.spacingVerticalL,
    },
    formSection: {
        display: 'flex',
        alignItems: 'flex-end',
        ...shorthands.gap(tokens.spacingHorizontalM),
    },
    inputField: {
        minWidth: '250px',
    },
    dropdown: {
        minWidth: '250px',
    },
    userCard: {
        padding: tokens.spacingVerticalM,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        marginBottom: tokens.spacingVerticalS,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    userEmail: {
        fontWeight: 600,
        fontSize: '1rem',
    },
    deleteButton: {
        marginLeft: '10px',
        transition: 'all 0.3s ease',
        ':hover': {
            backgroundColor: 'firebrick',
            color: 'white',
            transform: 'scale(1.05)',
        },
    },
    combobox: {
        minWidth: '300px',
    },
});

const ROLE_STORAGE_KEY = 'userRole';

const getCurrentRole = (): string[] => {
    try {
        const raw = sessionStorage.getItem(ROLE_STORAGE_KEY);
        console.log('DetailView getCurrentRole - raw sessionStorage:', raw);
        if (!raw) return [];

        const parsed = JSON.parse(raw) as string[];

        if (Array.isArray(parsed)) {
            return parsed.map((r) => r.trim());
        }

        return [];
    } catch (error) {
        console.log('DetailView Error parsing role:', error);
        return [];
    }
};

const RolePage: React.FC = () => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();
    const accountInfo = accounts[0] || null;
    const dataService = React.useMemo(() => {
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accountInfo]);
    const isAdmin = useState<boolean>(getCurrentRole().includes('Admin'));
    const [roles, setRoles] = useState([] as IDataItem[]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedPerson, setSelectedPerson] = useState<string>('');
    const [filteredPersons, setFilteredPersons] = useState<IDataItem[]>([]);
    const [emailInput, setEmailInput] = useState<string>('');
    const [selectedRole, setSelectedRole] = useState<string>('');
    const [selectedTab, setSelectedTab] = useState<string>('0');
    const [selectedTabName, setSelectedTabName] = useState<string>('');
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [assignedRoles, setAssignedRoles] = useState<IUserPermissionItem[]>([]);
    const [actionUser, setActionUser] = useState<IUserPermissionItem | null>(
        null
    );
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        const getRoles = async () => {
            if (!dataService) return;
            const response = await dataService.getAppRoles();
            if (response) {
                setSelectedTab(response[0]?.value || '0');
                setSelectedTabName(response[0]?.text || '');
                setRoles(response);
            }
        };
        const getAssignedRoles = async () => {
            if (!dataService) return;
            const response = await dataService.getRolesAppPermissions('');
            if (response) setAssignedRoles(response);
        };
        getRoles();
        getAssignedRoles();
    }, [dataService]);

    const getUsersByRole = (role: string) => {
        return assignedRoles.filter((ar) => ar.rollenId.toString() === role);
    };

    const onTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
        setSelectedTab(data.value as string);
        const selectedRole = roles.find((r) => r.value === data.value);
        setSelectedTabName(selectedRole ? selectedRole.text : '');
    };

    React.useEffect(() => {
        const fetchFilterOptions = async (): Promise<void> => {
            if (!dataService) return;
            if (searchQuery.length < 6) return;
            const response = await dataService.getMitarbeiterSearchByUpn(searchQuery);
            if (response) {
                setFilteredPersons(response);
            }
        };
        fetchFilterOptions();
    }, [dataService, searchQuery]);

    const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
        const target = event.currentTarget;
        setSearchQuery(target.value);
    };

    const handleSelect = (_: any, data: any) => {
        setSelectedPerson(data.optionValue || '');
        const person = filteredPersons.find((p) => p.value === data.optionValue);
        if (person) {
            setSearchQuery(person.value);
            setEmailInput(person.value);
        }
    };

    const handleAddRole = async () => {
        if (!dataService) return;
        setIsSubmitting(true);
        const roleId = roles.find((r) => r.text === selectedRole);
        await dataService.addUserRole({
            appId: 4,
            upn: emailInput,
            rollenId: roleId ? parseInt(roleId.value, 10) : 0,
        } as IUserPermissionItem);
        setEmailInput('');
        setSelectedRole('');
        setSearchQuery('');
        setSelectedPerson('');
        setFilteredPersons([]);
        const response = await dataService.getRolesAppPermissions('');
        if (response) setAssignedRoles(response);
        setIsSubmitting(false);
    };

    const handleDeleteRole = async (roleId: number) => {
        if (!dataService) return;
        await dataService.deleteUserRole(roleId);
        const response = await dataService.getRolesAppPermissions('');
        if (response) setAssignedRoles(response);
    };

    return (
        <div className={styles.container}>
            <h3>Rollenzuweisung</h3>

            <div className={styles.formSection}>
                <Field label="E-Mail-Adresse" required>
                    <Combobox
                        className={styles.combobox}
                        placeholder="Name oder E-Mail eingeben..."
                        value={searchQuery}
                        onInput={handleInput}
                        onOptionSelect={handleSelect}
                        selectedOptions={selectedPerson ? [selectedPerson] : []}
                    >
                        {filteredPersons.length > 0 ? (
                            filteredPersons.map((person) => (
                                <Option
                                    key={person.value}
                                    value={person.value}
                                    text={person.text}
                                >
                                    {person.text}
                                </Option>
                            ))
                        ) : (
                            <Option value="" disabled>
                                Keine Personen gefunden
                            </Option>
                        )}
                    </Combobox>
                </Field>

                <Field label="Rolle auswählen" required>
                    <Dropdown
                        className={styles.dropdown}
                        placeholder="Rolle wählen..."
                        value={selectedRole}
                        selectedOptions={selectedRole ? [selectedRole] : []}
                        onOptionSelect={(_, data) => {
                            setSelectedRole(data.optionValue || '');
                        }}
                    >
                        {roles.map((role) => (
                            <Option key={role.text} value={role.text} text={role.text}>
                                {role.text}
                            </Option>
                        ))}
                    </Dropdown>
                </Field>

                <Button
                    appearance="primary"
                    icon={<PersonAddRegular />}
                    onClick={handleAddRole}
                    disabled={
                        !isAdmin || !emailInput.trim() || !selectedRole || isSubmitting
                    }
                >
                    Rolle zuweisen
                </Button>
            </div>

            <div className={styles.overviewSection}>
                <Divider />
                <h3 style={{ marginTop: '20px' }}>Rollenübersicht</h3>
                <TabList selectedValue={selectedTab} onTabSelect={onTabSelect}>
                    <>
                        {roles.map((role) => (
                            <Tab key={role.value} value={role.value}>
                                {role.text} ({getUsersByRole(role.value).length})
                            </Tab>
                        ))}
                    </>
                </TabList>

                <div className={styles.tabContent}>
                    {getUsersByRole(selectedTab).length > 0 ? (
                        getUsersByRole(selectedTab).map((user) => (
                            <div key={user.itemId} className={styles.userCard}>
                                <div className={styles.userEmail}>{user.upn}</div>
                                <Button
                                    icon={<DeleteRegular />}
                                    title="Rolle entfernen"
                                    appearance="outline"
                                    onClick={() => {
                                        setActionUser(user);
                                        setIsDeleteDialogOpen(true);
                                    }}
                                    disabled={!isAdmin}
                                    className={styles.deleteButton}
                                />
                            </div>
                        ))
                    ) : (
                        <p
                            style={{
                                color: tokens.colorNeutralForeground3,
                                padding: tokens.spacingVerticalM,
                            }}
                        >
                            Keine Benutzer mit der Rolle vorhanden.
                        </p>
                    )}
                </div>
            </div>

            <Dialog
                open={isDeleteDialogOpen}
                onOpenChange={(_e, data) => setIsDeleteDialogOpen(data.open)}
            >
                <DialogSurface>
                    <DialogTitle style={{ marginBottom: '10px' }}>
                        Rolle entfernen
                    </DialogTitle>
                    <DialogContent>
                        {actionUser && (
                            <span>
                                Möchten Sie die Rolle <strong>{selectedTabName}</strong> von{' '}
                                <strong>{actionUser.upn}</strong> wirklich entfernen?
                            </span>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            appearance="secondary"
                            onClick={() => setIsDeleteDialogOpen(false)}
                        >
                            Abbrechen
                        </Button>
                        <Button
                            appearance="primary"
                            style={{ backgroundColor: 'firebrick' }}
                            onClick={() => {
                                if (actionUser) {
                                    handleDeleteRole(actionUser.itemId);
                                    setIsDeleteDialogOpen(false);
                                }
                            }}
                        >
                            Entfernen
                        </Button>
                    </DialogActions>
                </DialogSurface>
            </Dialog>
            <Toaster />
        </div>
    );
};

export default RolePage;
